/**
 * User Invitation Form Schema
 * Zod validation schema for inviting new users to a tenant
 */

import { z, validateSaudiPhone, normalizeSaudiPhoneForStorage } from '@spcc/core'
import type { StrapiRoleType, CreateUserRequest } from '../types/user.types'

// ===========================================
// Constants
// ===========================================

/**
 * Field name constants to prevent typos
 */
export const USER_FORM_FIELDS = {
  NAME: 'name',
  EMAIL: 'email',
  PASSWORD: 'password',
  PHONE: 'phone',
  ROLE: 'role',
  FACTORY_ID: 'factoryId',
  LOCATION_ID: 'locationId',
  DEPARTMENT: 'department',
  TENANT_ID: 'tenantId',
} as const

/**
 * Translation key constants
 */
export const USER_FORM_T_KEYS = {
  LABELS: {
    NAME: 'admin.user.form.name',
    EMAIL: 'admin.user.form.email',
    PASSWORD: 'admin.user.form.password',
    PHONE: 'admin.user.form.phone',
    ROLE: 'admin.user.form.role',
    FACTORY: 'admin.user.form.plant',
    ALL_PLANTS: 'admin.user.form.allPlants',
    LOCATION: 'admin.user.form.location',
    DEPARTMENT: 'admin.user.form.department',
    TENANT: 'admin.user.form.tenant',
  },
  PLACEHOLDERS: {
    NAME: 'admin.user.form.namePlaceholder',
    EMAIL: 'admin.user.form.emailPlaceholder',
    PASSWORD: 'admin.user.form.passwordPlaceholder',
    PHONE: 'admin.user.form.phonePlaceholder',
    FACTORY: 'admin.user.form.allPlants',
    LOCATION: 'admin.user.form.locationPlaceholder',
    DEPARTMENT: 'admin.user.form.departmentPlaceholder',
  },
  VALIDATION: {
    NAME_REQUIRED: 'admin.user.form.validation.nameRequired',
    EMAIL_REQUIRED: 'admin.user.form.validation.emailRequired',
    EMAIL_INVALID: 'admin.user.form.validation.emailInvalid',
    PASSWORD_REQUIRED: 'admin.user.form.validation.passwordRequired',
    PASSWORD_MIN: 'admin.user.form.validation.passwordMin',
    ROLE_REQUIRED: 'admin.user.form.validation.roleRequired',
    FACTORY_REQUIRED: 'admin.user.form.validation.plantRequiredForRole',
  },
  ROLE_OPTIONS: {
    KIOSK: 'admin.user.form.role.kiosk',
    SALES_OFFICE_USER: 'admin.user.form.role.salesOfficeUser',
    FINANCE_ADMIN: 'admin.user.form.role.financeAdmin',
    SECURITY_GUARD: 'admin.user.form.role.securityGuard',
    SAFETY_MANAGER: 'admin.user.form.role.safetyManager',
    SERVICES_MANAGER: 'admin.user.form.role.servicesManager',
    CUSTOMER_OPERATOR: 'admin.user.form.role.customerOperator',
    SCALE_OPERATOR: 'admin.user.form.role.scaleOperator',
    SCALE_ADMIN: 'admin.user.form.role.scaleAdmin',
    PACKHOUSE_MANAGER: 'admin.user.form.role.packhouseManager',
    EXECUTIVE: 'admin.user.form.role.executive',
    HEAD_OF_SALES: 'admin.user.form.role.headOfSales',
    SALES_MANAGER: 'admin.user.form.role.salesManager',
    SECURITY_MANAGER: 'admin.user.form.role.securityManager',
    IT: 'admin.user.form.role.it',
    VISITOR_APPROVAL_ADMIN: 'admin.user.form.role.visitorApprovalAdmin',
    SCM_ADMIN: 'admin.user.form.role.scmAdmin',
  },
} as const

// Only selected business roles can be invited from this drawer.
const invitableRoles = [
  'kiosk_user',
  'sales_office_user',
  'finance_admin',
  'security_guard',
  'safety_manager',
  'services_manager',
  'customer_operator',
  'scale_operator',
  'scale_admin',
  'packhouse_manager',
  'executive',
  'head_of_sales',
  'sales_manager',
  'security_manager',
  'it',
  'visitor_approval_admin',
  'scm_admin',
] as const
export type InvitableRole = (typeof invitableRoles)[number]

export function roleRequiresFactory(role: StrapiRoleType | undefined): boolean {
  return (
    role === 'kiosk_user' ||
    role === 'security_guard' ||
    role === 'safety_manager' ||
    role === 'services_manager' ||
    role === 'scale_operator' ||
    role === 'scale_admin' ||
    role === 'packhouse_manager' ||
    role === 'scm_admin'
  )
}

/** Roles that MUST have a location (factory or direct) — same set as factory-required roles. */
export function roleRequiresLocation(role: StrapiRoleType | undefined): boolean {
  return roleRequiresFactory(role)
}

/** Roles that may be assigned a direct OFFICE location instead of a plant. */
export function roleAllowsLocationOption(role: StrapiRoleType | undefined): boolean {
  return role === 'safety_manager' || role === 'services_manager' || role === 'visitor_approval_admin'
}

// ===========================================
// Zod Schema
// ===========================================

/**
 * Zod schema for user invitation form validation
 */
const baseUserFormSchema = z.object({
  name: z
    .string()
    .min(1, USER_FORM_T_KEYS.VALIDATION.NAME_REQUIRED)
    .max(255),
  email: z
    .string()
    .min(1, USER_FORM_T_KEYS.VALIDATION.EMAIL_REQUIRED)
    .email(USER_FORM_T_KEYS.VALIDATION.EMAIL_INVALID),
  password: z
    .string()
    .min(1, USER_FORM_T_KEYS.VALIDATION.PASSWORD_REQUIRED)
    .min(6, USER_FORM_T_KEYS.VALIDATION.PASSWORD_MIN),
  phone: z
    .string()
    .optional()
    .refine((v) => !v || v.trim() === '' || validateSaudiPhone(v.trim()).valid, 'validation.phoneSaudiFormat'),
  role: z.enum(invitableRoles as any, {}).optional(),
  factoryId: z.string().optional(),
  locationId: z.string().optional(),
  department: z.string().optional(),
  tenantId: z.string().optional(),
})

export const userFormSchema = baseUserFormSchema.superRefine((data, ctx) => {
  if (!data.role) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['role'],
      message: USER_FORM_T_KEYS.VALIDATION.ROLE_REQUIRED,
    })
    return
  }

  // Must have either factory or location for roles that require it
  if (roleRequiresFactory(data.role)) {
    const hasFactory = data.factoryId?.trim() && data.factoryId !== ALL_PLANTS_VALUE
    const hasLocation = data.locationId?.trim() && data.locationId !== ALL_PLANTS_VALUE
    if (!hasFactory && !hasLocation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['factoryId'],
        message: USER_FORM_T_KEYS.VALIDATION.FACTORY_REQUIRED,
      })
    }
  }
})

/**
 * Type inferred from the schema
 */
export type UserFormData = z.infer<typeof userFormSchema>

// ===========================================
// Default Values
// ===========================================

/**
 * Default values for user invitation form
 */
export const defaultUserFormValues: UserFormData = {
  name: '',
  email: '',
  password: '',
  phone: '',
  role: undefined,
  factoryId: '',
  locationId: '',
  department: '',
}

// ===========================================
// Transform Functions
// ===========================================

/**
 * Get default form values
 */
export function getDefaultValues(): UserFormData {
  return {
    ...defaultUserFormValues,
  }
}

/**
 * Map Strapi role to UI-friendly role
 */
function mapStrapiRoleToUserRole(strapiRole: InvitableRole): 'admin' | 'manager' | 'operator' | 'viewer' {
  const roleMap: Record<InvitableRole, 'admin' | 'manager' | 'operator' | 'viewer'> = {
    kiosk_user: 'operator',
    sales_office_user: 'manager',
    finance_admin: 'admin',
    security_guard: 'operator',
    safety_manager: 'operator',
    services_manager: 'manager',
    customer_operator: 'operator',
    scale_operator: 'operator',
    scale_admin: 'manager',
    packhouse_manager: 'operator',
    executive: 'viewer',
    head_of_sales: 'admin',
    sales_manager: 'manager',
    security_manager: 'manager',
    it: 'viewer',
    visitor_approval_admin: 'manager',
    scm_admin: 'manager',
  }
  return roleMap[strapiRole]
}

/**
 * Transform form data to API create request format
 */
export function formDataToCreateRequest(data: UserFormData): CreateUserRequest {
  if (!data.role) {
    throw new Error('Role is required')
  }
  const normalizedPhone = data.phone?.trim()
    ? (normalizeSaudiPhoneForStorage(data.phone.trim()) ?? data.phone.trim())
    : undefined

  return {
    name: data.name.trim(),
    email: data.email.trim().toLowerCase(),
    password: data.password,
    phone: normalizedPhone,
    role: mapStrapiRoleToUserRole(data.role),
    strapiRoleType: data.role as StrapiRoleType,
    factoryId:
      data.factoryId && data.factoryId !== ALL_PLANTS_VALUE
        ? data.factoryId.trim()
        : undefined,
    locationId:
      data.locationId && data.locationId !== ALL_PLANTS_VALUE
        ? data.locationId.trim()
        : undefined,
    department: data.department?.trim() || undefined,
    permissions: [], // Default permissions for new users
    tenantId: data.tenantId?.trim() || undefined,
  }
}

/**
 * Role options for the form dropdown
 */
/**
 * Sentinel value used by the factory <Select> to represent "no factory assigned"
 * (i.e. the user can see all plants). Radix selects cannot use an empty string
 * value, so we use this constant and translate it back to "no factory" on submit.
 */
export const ALL_PLANTS_VALUE = '__all_plants__'

export const INVITABLE_ROLE_OPTIONS: { value: InvitableRole; labelKey: string; label: string }[] = [
  { value: 'kiosk_user', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.KIOSK, label: 'Kiosk' },
  { value: 'sales_office_user', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SALES_OFFICE_USER, label: 'Sales Office User' },
  { value: 'finance_admin', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.FINANCE_ADMIN, label: 'Finance Admin' },
  { value: 'security_guard', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SECURITY_GUARD, label: 'Security Guard' },
  { value: 'safety_manager', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SAFETY_MANAGER, label: 'Safety Manager' },
  { value: 'services_manager', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SERVICES_MANAGER, label: 'Services Manager' },
  { value: 'customer_operator', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.CUSTOMER_OPERATOR, label: 'Customer Operator' },
  { value: 'scale_operator', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SCALE_OPERATOR, label: 'Scale Operator' },
  { value: 'scale_admin', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SCALE_ADMIN, label: 'Scale Admin' },
  { value: 'packhouse_manager', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.PACKHOUSE_MANAGER, label: 'Packhouse Manager' },
  { value: 'executive', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.EXECUTIVE, label: 'Executive' },
  { value: 'head_of_sales', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.HEAD_OF_SALES, label: 'Head of Sales' },
  { value: 'sales_manager', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SALES_MANAGER, label: 'Sales Manager' },
  { value: 'security_manager', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SECURITY_MANAGER, label: 'Security Manager' },
  { value: 'it', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.IT, label: 'IT' },
  { value: 'visitor_approval_admin', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.VISITOR_APPROVAL_ADMIN, label: 'Administrator (Visitor Approval)' },
  { value: 'scm_admin', labelKey: USER_FORM_T_KEYS.ROLE_OPTIONS.SCM_ADMIN, label: 'SCM Admin' },
]

export function getInvitableRoleOptionsForActor(actorRoleType: string | null | undefined) {
  if (actorRoleType === 'customer') {
    return INVITABLE_ROLE_OPTIONS.filter((option) => option.value === 'customer_operator')
  }
  return INVITABLE_ROLE_OPTIONS
}
